import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

export type ChartSelectionContextValue = {
  /** Set of currently selected keys. Empty set = all visible (nothing filtered out). */
  selectedKeys: Set<string>;
  /** Whether at least one key is explicitly selected (i.e. selection is active). */
  hasSelection: boolean;
  /** Returns true if the given key is selected (or if no selection is active). */
  isVisible: (key: string) => boolean;
  /** Toggle a key: add it if absent, remove it if present. */
  toggleKey: (key: string) => void;
  /** Select only the given keys (replaces current selection). */
  setSelectedKeys: (keys: string[]) => void;
  /** Clear the entire selection (show all elements). */
  clearAll: () => void;
  /** Select all the given keys at once. */
  selectAll: (keys: string[]) => void;
};

const ChartSelectionContext = createContext<
  ChartSelectionContextValue | undefined
>(undefined);

ChartSelectionContext.displayName = 'ChartSelectionContext';

type ChartSelectionProviderProps =
  | {
      children: ReactNode;
      /**
       * Optionally seed the initial selected keys.
       * Controlled mode: pass `selectedKeys` + `onSelectionChange` instead.
       */
      initialSelectedKeys?: string[];
      selectedKeys?: undefined;
      onSelectionChange?: (keys: string[]) => void;
    }
  | {
      children: ReactNode;
      initialSelectedKeys?: never;
      /**
       * Controlled selected keys. When provided, the provider becomes controlled
       * and `onSelectionChange` must also be provided.
       */
      selectedKeys: string[];
      /** Called whenever the selection changes in controlled mode. */
      onSelectionChange: (keys: string[]) => void;
    };

export const ChartSelectionProvider = ({
  children,
  initialSelectedKeys,
  selectedKeys: controlledKeys,
  onSelectionChange,
}: ChartSelectionProviderProps) => {
  const isControlled = controlledKeys !== undefined;

  const [internalKeys, setInternalKeys] = useState<Set<string>>(
    () => new Set(initialSelectedKeys ?? [])
  );

  const selectedKeys = useMemo(
    () => (isControlled ? new Set(controlledKeys) : internalKeys),
    [isControlled, controlledKeys, internalKeys]
  );

  const applyChange = useCallback(
    (next: Set<string>) => {
      if (!isControlled) {
        setInternalKeys(next);
      }
      onSelectionChange?.(Array.from(next));
    },
    [isControlled, onSelectionChange]
  );

  const toggleKey = useCallback(
    (key: string) => {
      const next = new Set(selectedKeys);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      applyChange(next);
    },
    [selectedKeys, applyChange]
  );

  const setSelectedKeys = useCallback(
    (keys: string[]) => {
      applyChange(new Set(keys));
    },
    [applyChange]
  );

  const clearAll = useCallback(() => {
    applyChange(new Set());
  }, [applyChange]);

  const selectAll = useCallback(
    (keys: string[]) => {
      applyChange(new Set(keys));
    },
    [applyChange]
  );

  const hasSelection = selectedKeys.size > 0;

  const isVisible = useCallback(
    (key: string) => !hasSelection || selectedKeys.has(key),
    [hasSelection, selectedKeys]
  );

  const value = useMemo<ChartSelectionContextValue>(
    () => ({
      selectedKeys,
      hasSelection,
      isVisible,
      toggleKey,
      setSelectedKeys,
      clearAll,
      selectAll,
    }),
    [
      selectedKeys,
      hasSelection,
      isVisible,
      toggleKey,
      setSelectedKeys,
      clearAll,
      selectAll,
    ]
  );

  return (
    <ChartSelectionContext.Provider value={value}>
      {children}
    </ChartSelectionContext.Provider>
  );
};

/**
 * Access the chart selection context. Returns `undefined` when used outside
 * a `ChartSelectionProvider` — chart elements should treat this as "show all".
 */
export const useChartSelection = (): ChartSelectionContextValue | undefined => {
  return useContext(ChartSelectionContext);
};
